import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import fleetApi from '../../../services/api.service';

const getErrorMessage = (error) =>
  error?.response?.data?.message || error?.message;

/**
 * API keys of a tenant ("Clés API" tab of the client record).
 *
 * Loads the list and the form options (scope catalog, rate limit bounds) when the tab opens.
 * Every action responds with the up-to-date list, which the hook keeps: the screen never has to
 * reload. Errors are shown as a notification and the actions then return null or false, like the
 * other hooks of the tool.
 *
 * @param {number} clientId - The tenant identifier
 */
export default function useClientApiKeys(clientId) {
  const [apiKeys, setApiKeys] = useState([]);
  const [options, setOptions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // True when the last load failed: the screen must say so instead of showing an empty list.
  const [loadError, setLoadError] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  // Key being updated or revoked: a single loading indicator per row.
  const [busyKeyId, setBusyKeyId] = useState(null);

  // A stale response that arrives after unmount (tab change) is ignored.
  useEffect(() => {
    let isActive = true;

    const load = async () => {
      try {
        setIsLoading(true);
        setLoadError(false);

        const [list, formOptions] = await Promise.all([
          fleetApi.get(`/super-admin/clients/${clientId}/api-keys`),
          fleetApi.get(`/super-admin/clients/${clientId}/api-keys/options`),
        ]);

        if (!isActive) return;

        setApiKeys(list.data.api_keys);
        setOptions(formOptions.data);
      } catch (error) {
        if (isActive) {
          setLoadError(true);
          toast.error(getErrorMessage(error));
        }
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    load();

    return () => {
      isActive = false;
    };
  }, [clientId, reloadCount]);

  const reload = () => setReloadCount((count) => count + 1);

  /**
   * Creates a key. The response carries the plaintext key: this is the only time it exists on the
   * screen side (one-time secret).
   * No success notification: the window that displays the key takes care of it, and a
   * notification (only one at a time) could cover it.
   * @param {object} payload - POST body (see buildCreatePayload)
   * @returns {Promise<{ key: string, apiKey: object }|null>} The plaintext key and its record, or
   *   null
   */
  const createApiKey = async (payload) => {
    try {
      setIsCreating(true);

      const response = await fleetApi.post(
        `/super-admin/clients/${clientId}/api-keys`,
        payload
      );

      setApiKeys(response.data.api_keys);

      return { key: response.data.key, apiKey: response.data.api_key };
    } catch (error) {
      toast.error(getErrorMessage(error));
      return null;
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Updates a key (label, expiry date, rate limit, scopes).
   * @param {number} keyId
   * @param {object} payload - PUT body (see buildUpdatePayload)
   * @returns {Promise<boolean>} true if the key was updated
   */
  const updateApiKey = async (keyId, payload) => {
    try {
      setBusyKeyId(keyId);

      const response = await fleetApi.put(
        `/super-admin/clients/${clientId}/api-keys/${keyId}`,
        payload
      );

      setApiKeys(response.data.api_keys);
      toast.success(response.data.message);

      return true;
    } catch (error) {
      toast.error(getErrorMessage(error));
      return false;
    } finally {
      setBusyKeyId(null);
    }
  };

  /**
   * Revokes a key: it is rejected immediately and permanently.
   * @param {number} keyId
   * @returns {Promise<boolean>} true if the key was revoked
   */
  const revokeApiKey = async (keyId) => {
    try {
      setBusyKeyId(keyId);

      const response = await fleetApi.delete(
        `/super-admin/clients/${clientId}/api-keys/${keyId}`
      );

      setApiKeys(response.data.api_keys);
      toast.success(response.data.message);

      return true;
    } catch (error) {
      toast.error(getErrorMessage(error));
      return false;
    } finally {
      setBusyKeyId(null);
    }
  };

  return {
    apiKeys,
    options,
    isLoading,
    loadError,
    reload,
    isCreating,
    busyKeyId,
    createApiKey,
    updateApiKey,
    revokeApiKey,
  };
}
